import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Users, UserCheck, Settings, Home, LogOut, PanelLeftClose, PanelLeftOpen, Menu, ChevronDown, FileCheck2, Presentation, Banknote, AlertTriangle, GraduationCap, ClipboardCheck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { LogoFull, LogoMark, Wordmark } from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeToggle from '../components/ThemeToggle';
import NotifBell from '../components/NotifBell';
import OnboardingHint from '../components/OnboardingHint';
import CommandPalette, { CommandTrigger } from '../components/CommandPalette';
import UserAvatar from '../components/UserAvatar';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

const NAV = {
  bendahara: [
    { group: 'utama', items: [{ to: '/bendahara', key: 'dashboard', icon: LayoutDashboard, end: true }] },
    { group: 'pengurusan', items: [
      { to: '/bendahara/pelajar', key: 'pelajarKelas', icon: Users },
      { to: '/bendahara/pengguna', key: 'pengguna', icon: UserCheck },
      { to: '/bendahara/pengesahan', key: 'pengesahan', icon: FileCheck2 },
      { to: '/bendahara/tarif', key: 'tetapan', icon: Settings },
    ] },
    { group: 'laporan', items: [
      { to: '/bendahara/laporan/mesyuarat', key: 'lapMesyuarat', icon: Presentation },
      { to: '/bendahara/laporan/kutipan', key: 'lapKutipan', icon: Banknote },
      { to: '/bendahara/laporan/tunggakan', key: 'lapTunggakan', icon: AlertTriangle },
      { to: '/bendahara/laporan/pelajar', key: 'lapPelajar', icon: GraduationCap },
      { to: '/bendahara/laporan/pengesahan', key: 'lapPengesahan', icon: ClipboardCheck },
    ] },
  ],
  guru: [{ group: 'utama', items: [{ to: '/guru', key: 'dashboardKelas', icon: LayoutDashboard, end: true }] }],
  orangtua: [{ group: 'utama', items: [{ to: '/orangtua', key: 'anakSaya', icon: Home, end: true }] }],
};

function NavItems({ role, collapsed, onNavigate }) {
  const { t } = useTranslation();
  const groups = NAV[role] || [];
  return (
    <nav className="flex flex-col gap-5 px-3">
      {groups.map((g) => (
        <div key={g.group}>
          {!collapsed && (
            <p className="px-3 mb-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{t(g.group)}</p>
          )}
          <div className="flex flex-col gap-1">
            {g.items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={onNavigate}
                data-testid={`nav-${it.key}`}
                title={collapsed ? t(it.key) : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 h-11 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
                    isActive ? 'bg-brand-light text-brand font-semibold' : 'text-muted-foreground hover:bg-muted'
                  } ${collapsed ? 'justify-center' : ''}`
                }
              >
                <it.icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span>{t(it.key)}</span>}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export default function AppShell() {
  const { profile, school, role, signOut } = useAuth();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const sidebarInner = (onNavigate) => (
    <div className="flex h-full flex-col">
      <div className={`flex items-center h-16 px-4 border-b border-border ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {collapsed ? <LogoMark size={34} /> : <LogoFull imgSize={36} textSize="text-xl" />}
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-5 custom-scrollbar">
        <NavItems role={role} collapsed={collapsed} onNavigate={onNavigate} />
      </div>
      <div className="p-3 border-t border-border">
        <button
          onClick={() => setCollapsed((c) => !c)}
          data-testid="sidebar-collapse"
          className="hidden lg:flex items-center gap-3 w-full rounded-xl px-3 h-11 text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
        >
          {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <><PanelLeftClose className="h-5 w-5" /><span>Lipat</span></>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <CommandPalette />
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex fixed inset-y-0 left-0 z-30 flex-col bg-card border-r border-border transition-all duration-200 ${collapsed ? 'w-[76px]' : 'w-64'}`}
      >
        {sidebarInner()}
      </aside>

      <div className={`transition-all duration-200 ${collapsed ? 'lg:pl-[76px]' : 'lg:pl-64'}`}>
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex items-center gap-3 h-16 px-4 sm:px-6 bg-card/90 backdrop-blur border-b border-border">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden h-11 w-11 flex items-center justify-center rounded-lg border border-border" data-testid="mobile-menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64">
              {sidebarInner(() => setMobileOpen(false))}
            </SheetContent>
          </Sheet>

          <div className="lg:hidden"><Wordmark size="text-lg" /></div>

          <div className="hidden md:flex flex-col leading-tight mr-auto">
            <span className="text-sm font-bold text-foreground">{school?.nama || 'MTSRpay'}</span>
            <span className="text-xs text-muted-foreground">{t(`role_${role}`)}</span>
          </div>

          <div className="hidden md:block ml-auto"><CommandTrigger /></div>
          <NotifBell />
          <ThemeToggle />
          <LanguageSwitcher />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 h-11 pl-1 pr-2 rounded-lg hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-testid="user-menu">
                <UserAvatar nama={profile?.nama} size={36} />
                <ChevronDown className="h-4 w-4 text-muted-foreground hidden sm:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="font-semibold text-foreground">{profile?.nama}</p>
                <p className="text-xs font-normal text-muted-foreground">{t(`role_${role}`)}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut} data-testid="logout-btn" className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400">
                <LogOut className="mr-2 h-4 w-4" />{t('logKeluar')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
          <OnboardingHint role={role} />
          <div key={location.pathname} className="page-enter">
            <React.Suspense fallback={<div className="py-12 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-brand/20 border-t-brand animate-spin" /></div>}>
              <Outlet />
            </React.Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
